import {useEffect, useRef} from 'react';

import type Scrollbars from 'react-custom-scrollbars-2';

/**
 * Custom hook for managing the behavior of an option list, particularly handling scrolling behavior based on whether the list is displayed on top.
 * The hook provides a reference to a scrollable container and adjusts the scroll position if the list is positioned at the top.
 *
 * @param isOnTop A boolean indicating whether the option list is displayed on top. If true, the scroll will automatically move to the bottom.
 * @returns An object containing the reference to the scrollable container.
 *
 * @example
 * ```tsx
 * const {scrollRef} = useOptionList(true);
 * <CustomScrollContainer ref={scrollRef}>...</CustomScrollContainer>
 * ```
 */
export const useOptionList = (isOnTop: boolean) => {
    const scrollRef = useRef<Scrollbars>(null);

    useEffect(() => {
        if (scrollRef.current && isOnTop) {
            scrollRef.current.scrollToBottom();
        }
    }, [isOnTop]);

    return {scrollRef};
};